import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { PlatformBoot } from "./api/PlatformBoot";
import { RequireAuth } from "./auth/RequireAuth";
import { useCurrentUser } from "./auth/me";
import { Shell } from "./components/Shell";
import { Availability } from "./pages/Availability";
import { BrandDetail } from "./pages/BrandDetail";
import { Brands } from "./pages/Brands";
import { Compare } from "./pages/Compare";
import { JobDetail } from "./pages/JobDetail";
import { Jobs } from "./pages/Jobs";
import { PlatformStatus } from "./pages/PlatformStatus";
import { Platforms } from "./pages/Platforms";
import { ProbeLocations } from "./pages/ProbeLocations";
import { Proxies } from "./pages/Proxies";
import { Rank } from "./pages/Rank";
import { Shelf } from "./pages/Shelf";
import { Waf } from "./pages/Waf";
import { RunMonitor } from "./pages/RunMonitor";
import { Runs } from "./pages/Runs";
import { SkuDetail } from "./pages/SkuDetail";
import { Snapshots } from "./pages/Snapshots";
import { SkuStoreDetail } from "./pages/SkuStoreDetail";
import { StoreDetail } from "./pages/StoreDetail";
import { Stores } from "./pages/Stores";
import { Settings } from "./pages/Settings";
import { Taxonomy } from "./pages/Taxonomy";
import { TaxonomyReview } from "./pages/TaxonomyReview";
import { Users } from "./pages/Users";

function SuperuserOnly({ children }: { children: ReactNode }) {
  return useCurrentUser().is_superuser ? <>{children}</> : <Navigate to="/status" replace />;
}

export default function App() {
  return (
    <RequireAuth>
      <PlatformBoot>
        <Shell>
          <Routes>
          <Route path="/" element={<Navigate to="/status" replace />} />
          <Route path="/status" element={<PlatformStatus />} />
          <Route path="/platforms" element={<Platforms />} />
          <Route path="/jobs" element={<Jobs />} />
          <Route path="/jobs/:jobId" element={<JobDetail />} />
          <Route path="/runs" element={<Runs />} />
          <Route path="/runs/:runId" element={<RunMonitor />} />
          <Route path="/availability" element={<Availability />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/proxies" element={<Proxies />} />
          <Route path="/waf" element={<Waf />} />
          <Route path="/brands" element={<Brands />} />
          <Route path="/rank" element={<Rank />} />
          {/* The keyword is one path segment; the category id is a splat because a
              platform writes it with a slash in it ("1237/940" is one address). */}
          <Route path="/rank/keywords/:keyword" element={<Shelf kind="search" />} />
          <Route path="/rank/categories/:platform/*" element={<Shelf kind="category" />} />
          {/* Nested under /brands (not a top-level nav item); React Router ranks this
              static path above the dynamic :brandId regardless of order. */}
          <Route path="/brands/probe-locations" element={<ProbeLocations />} />
          <Route path="/brands/:brandId" element={<BrandDetail />} />
          <Route path="/skus/:platform/:productId" element={<SkuDetail />} />
          <Route path="/skus/:platform/:productId/stores/:storeId" element={<SkuStoreDetail />} />
          <Route path="/stores" element={<Stores />} />
          <Route path="/stores/:platform/:storeId" element={<StoreDetail />} />
          {/* Static path ranks above nothing dynamic here, but keep Review before the base. */}
          <Route path="/taxonomy/review" element={<TaxonomyReview />} />
          <Route path="/taxonomy" element={<Taxonomy />} />
          <Route path="/snapshots" element={<Snapshots />} />
          <Route path="/settings" element={<Settings />} />
          <Route
            path="/users"
            element={
              <SuperuserOnly>
                <Users />
              </SuperuserOnly>
            }
          />
          <Route path="*" element={<Navigate to="/status" replace />} />
          </Routes>
        </Shell>
      </PlatformBoot>
    </RequireAuth>
  );
}
